import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const CollapsibleSection = ({ title, defaultOpen = false, children, badge }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="debug-section">
            <button className="debug-section-header" onClick={() => setOpen(!open)}>
                <span className="debug-section-arrow">{open ? '▼' : '▶'}</span>
                <span className="debug-section-title">{title}</span>
                {badge && <span className="debug-section-badge">{badge}</span>}
            </button>
            {open && <div className="debug-section-body">{children}</div>}
        </div>
    );
};

const JsonBlock = ({ data }) => (
    <pre className="debug-json-block">
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
    </pre>
);

const AgentDebugPanel = ({ debugData, visible, onClose }) => {
    if (!visible || !debugData) return null;

    const { system_instruction, model, user_prompt, available_tools, turns, total_turns, response_length } = debugData;

    return createPortal(
        <div className="debug-panel-overlay">
            <div className="debug-panel">
                <div className="debug-panel-header">
                    <h3>Agent Debug Inspector</h3>
                    <button className="debug-panel-close" onClick={onClose}>&times;</button>
                </div>
                <div className="debug-panel-content">
                    <div className="debug-meta-row">
                        <span className="debug-meta-label">Model:</span>
                        <span className="debug-meta-value">{model}</span>
                        <span className="debug-meta-label">Turns:</span>
                        <span className="debug-meta-value">{total_turns}</span>
                        <span className="debug-meta-label">Response:</span>
                        <span className="debug-meta-value">{response_length} chars</span>
                    </div>

                    <CollapsibleSection title="System Instruction" defaultOpen>
                        <JsonBlock data={system_instruction} />
                    </CollapsibleSection>

                    <CollapsibleSection title="User Prompt" defaultOpen>
                        <JsonBlock data={user_prompt} />
                    </CollapsibleSection>

                    <CollapsibleSection title="Available Tools" badge={available_tools?.length}>
                        <ul className="debug-tool-list">
                            {available_tools?.map((t, i) => <li key={i}><code>{t}</code></li>)}
                        </ul>
                    </CollapsibleSection>

                    {turns?.length > 0 && (
                        <CollapsibleSection title="Tool Call Turns" badge={turns.length} defaultOpen>
                            {turns.map((turn, ti) => (
                                <CollapsibleSection key={ti} title={`Turn ${turn.turn}`} badge={`${turn.tool_calls.length} call(s)`} defaultOpen>
                                    {turn.reasoning && turn.reasoning.length > 0 && (
                                        <div className="debug-reasoning">
                                            <div className="debug-reasoning-label">
                                                {turn.reasoning.some(r => r.is_thought) ? 'LLM Thinking' : 'LLM Reasoning'}
                                            </div>
                                            {turn.reasoning.map((r, ri) => (
                                                <p key={ri} className={`debug-reasoning-text ${r.is_thought ? 'debug-thought' : ''}`}>
                                                    {r.is_thought && <span className="debug-thought-badge">thought</span>}
                                                    {r.text}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                    {turn.tool_calls.map((tc, ci) => (
                                        <div key={ci} className="debug-tool-call">
                                            <div className="debug-tool-call-header">
                                                <code className="debug-fn-name">{tc.function}</code>
                                            </div>
                                            <CollapsibleSection title="Arguments">
                                                <JsonBlock data={tc.args} />
                                            </CollapsibleSection>
                                            <CollapsibleSection title={`Response (${tc.response_length} chars)`}>
                                                <JsonBlock data={tc.response_preview} />
                                            </CollapsibleSection>
                                        </div>
                                    ))}
                                </CollapsibleSection>
                            ))}
                        </CollapsibleSection>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AgentDebugPanel;
